import { forwardRef, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/platform/lib/cn";

const badgeVariants = cva(
  [
    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1",
    "text-[length:var(--text-caption)] font-semibold",
    "tracking-[-0.005em] leading-none",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "border-[--color-border] bg-[color:var(--color-muted)]",
          "text-[color:var(--color-muted-foreground)]",
        ].join(" "),
        accent: [
          "border-transparent bg-[color:var(--color-accent-soft)]",
          "text-[color:var(--color-accent-soft-foreground)]",
        ].join(" "),
        accentSolid: [
          "border-transparent bg-[color:var(--color-accent)] text-white",
        ].join(" "),
        pink: [
          "border-transparent bg-[color:var(--color-pink-soft)]",
          "text-[color:var(--color-pink-deep)]",
        ].join(" "),
        mint: [
          "border-transparent bg-[color:var(--color-mint-soft)]",
          "text-[color:var(--color-mint-deep)]",
        ].join(" "),
        yellow: [
          "border-transparent bg-[color:var(--color-yellow-soft)]",
          "text-[color:var(--color-yellow-deep)]",
        ].join(" "),
        coral: [
          "border-transparent bg-[color:var(--color-coral-soft)]",
          "text-[color:var(--color-coral-deep)]",
        ].join(" "),
        sky: [
          "border-transparent bg-[color:var(--color-sky-soft)]",
          "text-[color:var(--color-sky-deep)]",
        ].join(" "),
        success: [
          "border-transparent bg-[color:var(--color-success-soft)]",
          "text-[color:var(--color-success)]",
        ].join(" "),
        warning: [
          "border-transparent bg-[color:var(--color-warning-soft)]",
          "text-[color:var(--color-warning)]",
        ].join(" "),
        danger: [
          "border-transparent bg-[color:var(--color-danger-soft)]",
          "text-[color:var(--color-danger)]",
        ].join(" "),
        outline: [
          "border-[--color-border] bg-transparent",
          "text-[color:var(--color-muted-foreground)]",
        ].join(" "),
        solid: [
          "border-transparent bg-[color:var(--color-foreground)]",
          "text-[color:var(--color-background)]",
        ].join(" "),
        onDark: [
          "border-2 border-white/35 bg-white/18 backdrop-blur-sm text-white",
          "shadow-sm",
        ].join(" "),
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(({ className, variant, ...props }, ref) => (
  <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
));
Badge.displayName = "Badge";
